import * as THREE from 'three';
import { createToonGradientMap } from './materials.js';

/**
 * Builds a procedural low-poly Fortnite-style humanoid THREE.Group.
 * All geometry is BoxGeometry / CylinderGeometry only — no external models.
 * Includes helmet, backpack, belt, and boots for readable silhouette at distance.
 */
export function buildHumanoidGroup(hue: number, toonGrad: THREE.DataTexture): THREE.Group {
  const group = new THREE.Group();

  // ── Materials ────────────────────────────────────────────────────────────
  const skinColor = new THREE.Color().setHSL(0.07, 0.52, 0.64);
  const bodyColor = new THREE.Color().setHSL(hue, 0.72, 0.48);
  const accentColor = new THREE.Color().setHSL((hue + 0.08) % 1, 0.78, 0.42);
  const limbColor = new THREE.Color().setHSL((hue + 0.04) % 1, 0.62, 0.36);
  const darkArmor = new THREE.Color().setHSL(hue, 0.35, 0.18);
  const bootColor = new THREE.Color().setHSL(0.08, 0.25, 0.16);

  const mkMat = (c: THREE.Color, opts?: { emissive?: number; emissiveIntensity?: number }) => {
    const m = new THREE.MeshToonMaterial({ color: c.getHex(), gradientMap: toonGrad });
    if (opts?.emissive !== undefined) m.emissive.setHex(opts.emissive);
    if (opts?.emissiveIntensity !== undefined) m.emissiveIntensity = opts.emissiveIntensity;
    return m;
  };

  const skinMat = mkMat(skinColor);
  const bodyMat = mkMat(bodyColor, { emissive: bodyColor.getHex(), emissiveIntensity: 0.08 });
  const accentMat = mkMat(accentColor, { emissive: accentColor.getHex(), emissiveIntensity: 0.14 });
  const limbMat = mkMat(limbColor);
  const armorMat = mkMat(darkArmor);
  const bootMat = mkMat(bootColor);

  // ── Helper ────────────────────────────────────────────────────────────────
  const box = (w: number, h: number, d: number, mat: THREE.MeshToonMaterial): THREE.Mesh => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
    m.castShadow = true;
    m.receiveShadow = true;
    return m;
  };

  // ── Head ──────────────────────────────────────────────────────────────────
  const head = box(0.36, 0.36, 0.36, skinMat);
  head.position.set(0, 1.76, 0);
  group.add(head);

  // Visor / helmet crest (readable silhouette)
  const helm = box(0.4, 0.14, 0.42, armorMat);
  helm.position.set(0, 1.96, 0.02);
  group.add(helm);
  const visor = box(0.32, 0.1, 0.08, accentMat);
  visor.position.set(0, 1.78, 0.2);
  group.add(visor);

  // ── Torso ─────────────────────────────────────────────────────────────────
  const torso = box(0.58, 0.72, 0.34, bodyMat);
  torso.position.set(0, 1.22, 0);
  group.add(torso);

  // Chest plate + belt
  const chest = box(0.5, 0.28, 0.36, armorMat);
  chest.position.set(0, 1.35, 0.02);
  group.add(chest);
  const belt = box(0.56, 0.1, 0.36, accentMat);
  belt.position.set(0, 0.9, 0);
  group.add(belt);

  // Backpack (iconic BR silhouette)
  const pack = box(0.36, 0.42, 0.22, armorMat);
  pack.position.set(0, 1.28, -0.26);
  group.add(pack);
  const packStrap = box(0.08, 0.5, 0.06, accentMat);
  packStrap.position.set(0.18, 1.3, -0.12);
  group.add(packStrap);
  const packStrapL = box(0.08, 0.5, 0.06, accentMat);
  packStrapL.position.set(-0.18, 1.3, -0.12);
  group.add(packStrapL);

  // ── Upper Arms ────────────────────────────────────────────────────────────
  const uArmL = box(0.19, 0.5, 0.19, limbMat);
  uArmL.position.set(-0.4, 1.15, 0);
  group.add(uArmL);

  const uArmR = box(0.19, 0.5, 0.19, limbMat);
  uArmR.position.set(0.4, 1.15, 0);
  group.add(uArmR);

  // Shoulder pads
  const padL = box(0.22, 0.16, 0.22, armorMat);
  padL.position.set(-0.42, 1.42, 0);
  group.add(padL);
  const padR = box(0.22, 0.16, 0.22, armorMat);
  padR.position.set(0.42, 1.42, 0);
  group.add(padR);

  // ── Forearms ──────────────────────────────────────────────────────────────
  const fArmL = box(0.17, 0.44, 0.17, skinMat);
  fArmL.position.set(-0.4, 0.72, 0);
  group.add(fArmL);

  const fArmR = box(0.17, 0.44, 0.17, skinMat);
  fArmR.position.set(0.4, 0.72, 0);
  group.add(fArmR);

  // ── Upper Legs ────────────────────────────────────────────────────────────
  const uLegL = box(0.21, 0.55, 0.21, limbMat);
  uLegL.position.set(-0.16, 0.595, 0);
  group.add(uLegL);

  const uLegR = box(0.21, 0.55, 0.21, limbMat);
  uLegR.position.set(0.16, 0.595, 0);
  group.add(uLegR);

  // ── Lower Legs + boots ────────────────────────────────────────────────────
  const lLegL = box(0.18, 0.42, 0.18, skinMat);
  lLegL.position.set(-0.16, 0.2, 0);
  group.add(lLegL);

  const lLegR = box(0.18, 0.42, 0.18, skinMat);
  lLegR.position.set(0.16, 0.2, 0);
  group.add(lLegR);

  const bootL = box(0.2, 0.14, 0.28, bootMat);
  bootL.position.set(-0.16, 0.04, 0.04);
  group.add(bootL);
  const bootR = box(0.2, 0.14, 0.28, bootMat);
  bootR.position.set(0.16, 0.04, 0.04);
  group.add(bootR);

  // Tag parts for animation retrieval
  uArmL.userData.part = 'uArmL';
  uArmR.userData.part = 'uArmR';
  fArmL.userData.part = 'fArmL';
  fArmR.userData.part = 'fArmR';
  uLegL.userData.part = 'uLegL';
  uLegR.userData.part = 'uLegR';
  lLegL.userData.part = 'lLegL';
  lLegR.userData.part = 'lLegR';
  torso.userData.part = 'torso';

  return group;
}

// ── Default shared gradient (lazy-initialized) ────────────────────────────
let _sharedGrad: THREE.DataTexture | null = null;
function sharedGrad(): THREE.DataTexture {
  if (!_sharedGrad) _sharedGrad = createToonGradientMap();
  return _sharedGrad;
}

/**
 * Fortnite-style blocky humanoid character with animation and nameplate.
 */
export class FpsCharacterModel {
  public readonly group: THREE.Group;

  private walkPhase = 0;
  private toonGrad: THREE.DataTexture;

  // Named-part caches
  private uArmL!: THREE.Mesh;
  private uArmR!: THREE.Mesh;
  private fArmL!: THREE.Mesh;
  private fArmR!: THREE.Mesh;
  private uLegL!: THREE.Mesh;
  private uLegR!: THREE.Mesh;
  private lLegL!: THREE.Mesh;
  private lLegR!: THREE.Mesh;
  private torso!: THREE.Mesh;

  // Nameplate DOM element
  private nameplate: HTMLDivElement;
  private nameplateVisible = false;

  constructor(private scene: THREE.Scene, hue: number) {
    this.toonGrad = sharedGrad();
    this.group = buildHumanoidGroup(hue, this.toonGrad);
    scene.add(this.group);

    // Cache part refs
    for (const child of this.group.children) {
      const p = (child as THREE.Mesh).userData?.part;
      if (p === 'uArmL') this.uArmL = child as THREE.Mesh;
      else if (p === 'uArmR') this.uArmR = child as THREE.Mesh;
      else if (p === 'fArmL') this.fArmL = child as THREE.Mesh;
      else if (p === 'fArmR') this.fArmR = child as THREE.Mesh;
      else if (p === 'uLegL') this.uLegL = child as THREE.Mesh;
      else if (p === 'uLegR') this.uLegR = child as THREE.Mesh;
      else if (p === 'lLegL') this.lLegL = child as THREE.Mesh;
      else if (p === 'lLegR') this.lLegR = child as THREE.Mesh;
      else if (p === 'torso') this.torso = child as THREE.Mesh;
    }

    // Nameplate
    this.nameplate = document.createElement('div');
    this.nameplate.style.cssText =
      'position:fixed;transform:translate(-50%,-100%);padding:2px 8px;border-radius:4px;' +
      'background:rgba(8,12,20,0.78);color:#fff;font:700 10px system-ui,sans-serif;' +
      'text-shadow:0 1px 3px #000;pointer-events:none;white-space:nowrap;' +
      'border:1px solid rgba(255,255,255,0.18);z-index:40;display:none';
    document.body.appendChild(this.nameplate);
  }

  /** Set the floating nameplate text. Pass empty string to hide. */
  public setName(name: string): void {
    this.nameplate.textContent = name;
    this.nameplateVisible = name.length > 0;
    this.nameplate.style.display = this.nameplateVisible ? 'block' : 'none';
  }

  public setVisible(v: boolean): void {
    this.group.visible = v;
    this.nameplate.style.display = v && this.nameplateVisible ? 'block' : 'none';
  }

  /**
   * Animate limbs based on velocity, update world transform for the group.
   * @param dt - delta time in seconds
   * @param velocity - world-space velocity (used for swing amplitude)
   * @param isGrounded - whether character is on the ground
   * @param yaw - character facing angle in radians
   */
  public update(dt: number, velocity: THREE.Vector3, isGrounded: boolean, yaw: number): void {
    const speed = Math.sqrt(velocity.x * velocity.x + velocity.z * velocity.z);
    const swingAmp = isGrounded ? Math.min(speed * 0.18, 0.55) : 0.2;

    if (speed > 0.1 && isGrounded) {
      this.walkPhase += dt * speed * 3.2;
    }

    const swing = Math.sin(this.walkPhase) * swingAmp;

    // Arms swing opposite to legs
    if (this.uArmL) this.uArmL.rotation.x = -swing;
    if (this.uArmR) this.uArmR.rotation.x = swing;
    if (this.fArmL) this.fArmL.rotation.x = -swing * 0.6;
    if (this.fArmR) this.fArmR.rotation.x = swing * 0.6;

    // Legs
    if (this.uLegL) this.uLegL.rotation.x = swing;
    if (this.uLegR) this.uLegR.rotation.x = -swing;
    if (this.lLegL) this.lLegL.rotation.x = Math.max(0, swing) * 0.5;
    if (this.lLegR) this.lLegR.rotation.x = Math.max(0, -swing) * 0.5;

    // Slight torso tilt forward when running
    if (this.torso) {
      const runTilt = Math.min(speed * 0.04, 0.18);
      this.torso.rotation.x = isGrounded ? runTilt : 0;
    }

    // Apply yaw to group
    this.group.rotation.y = yaw;
  }

  /**
   * Project the nameplate to screen-space using the given camera.
   * Call this once per frame after update().
   */
  public updateNameplate(camera: THREE.Camera, container: HTMLElement): void {
    if (!this.nameplateVisible) return;

    const worldPos = new THREE.Vector3();
    this.group.getWorldPosition(worldPos);
    worldPos.y += 2.15; // above head

    const projected = worldPos.clone().project(camera);

    // Convert NDC → CSS px
    const hw = container.clientWidth * 0.5;
    const hh = container.clientHeight * 0.5;
    const sx = projected.x * hw + hw;
    const sy = -projected.y * hh + hh;

    // Hide if behind camera
    if (projected.z > 1) {
      this.nameplate.style.display = 'none';
      return;
    }

    this.nameplate.style.display = 'block';
    this.nameplate.style.left = `${sx}px`;
    this.nameplate.style.top = `${sy}px`;
  }

  /** Remove from scene and dispose all GPU resources. */
  public dispose(): void {
    this.scene.remove(this.group);
    this.group.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((m) => m.dispose());
      } else if (mesh.material) {
        (mesh.material as THREE.Material).dispose();
      }
    });
    if (this.nameplate.parentNode) {
      this.nameplate.parentNode.removeChild(this.nameplate);
    }
  }
}
